import {
  IParamsObterPesquisaNps,
  IParamsSavePesquisaNps
} from "@/core/services/shared/pesquisaNPS/type";
import { IPesquisaNpsHandler } from "./protocols/IPesquisaNpsHandler";
import { inject, injectable } from "inversify";
import { DIMixins, DITypes, LocalStorageKeys } from "@/core/@types/types";
import { type IPesquisaNpsGateway } from "@/core/services/shared/pesquisaNPS/IPesquisaNpsGateway";
import { type IStorage } from "@/core/plugins/storage/Storage";
import { capitalizeFirstLetter } from "@/core/common/utils";
import { type IUserProfileStorageMixin } from "@/core/plugins/storage/UserProfileStorageMixin";
import { IParamsNps, IUserNpsStore } from "@/core/store/pesquisaNps/types";
import { usePesquisaNpsStore } from "@/core/store/pesquisaNps/usePesquisaNpsStore";
import { ECategoriaNps, ETipoProduto, obterCategoriaNps } from "./types";
import { type IToastNotification } from "@/core/composables/notification/types";
import { type IFinancasFeatureGateway } from "@/features/financas/services/ports/FinancasFeatureGateway";
import { diContainer } from "@/core/di";

@injectable()
export default class PesquisaNpsHandler implements IPesquisaNpsHandler {
  @inject(DIMixins.USER_PROFILE_STORAGE)
  userProfileMixin!: IUserProfileStorageMixin;

  @inject(DITypes.PESQUISA_NPS_GATEWAY)
  private pesquisaNpsGateway!: IPesquisaNpsGateway;

  @inject(DITypes.NOTIFICATION)
  private notification!: IToastNotification<string>;

  @inject(DITypes.STORAGE)
  private storage!: IStorage;

  private userNpsStore: IUserNpsStore = usePesquisaNpsStore();

  private campanhaAtiva = false;

  setNota(value: number | undefined) {
    this.userNpsStore.params.nota = value;
  }

  setComentario(value: string) {
    this.userNpsStore.params.comentario = value;
  }

  setTelefone(value: string) {
    this.userNpsStore.params.telefone = value;
  }

  setAceitaContato(value: boolean) {
    this.userNpsStore.params.aceitaContato = value;
  }

  setIsWhatsapp(value: boolean) {
    this.userNpsStore.params.isWhatsapp = value;
  }

  setUpdateStep(value: number) {
    this.userNpsStore.params.step = value;
  }

  obterCategoria(): ECategoriaNps | undefined {
    return obterCategoriaNps(this.userNpsStore.params.nota);
  }

  handleProduto(
    dados: IParamsObterPesquisaNps[] | null | undefined,
    hasContabil: boolean
  ) {
    const isFinancas = !window.location.href.includes("contabil");
    const isContabil = window.location.href.includes("contabil") && hasContabil;

    const produto = isFinancas
      ? ETipoProduto.financas
      : isContabil
        ? ETipoProduto.contabil
        : undefined;

    if (produto == undefined) return false;

    const tipoDoProduto = dados?.find(
      (item: IParamsObterPesquisaNps) => item.tipoCampanha == produto
    );

    if (!tipoDoProduto) return false;

    const perfil = this.userProfileMixin.handleGetUserProfile();
    const nomeDoGrupo = perfil?.Nome || "";

    this.userNpsStore.params.campanhaId = tipoDoProduto?.campanhaId;
    this.userNpsStore.params.exibirCampanha =
      tipoDoProduto.exibirCampanha || false;
    this.userNpsStore.params.cliente = capitalizeFirstLetter(nomeDoGrupo);
    this.userNpsStore.params.tipoCampanha = tipoDoProduto?.tipoCampanha;
    this.userNpsStore.params.telefone = perfil?.Telefone || "";
    this.userNpsStore.params.aceitaContato = false;
    this.userNpsStore.params.isWhatsapp = perfil?.isWhatsApp || false;

    return tipoDoProduto.exibirCampanha || false;
  }

  async handleUsuario(hasContabil: boolean) {
    const dados = await this.pesquisaNpsGateway.handleObterDadosPesquisa();

    if (!this.userProfileMixin.handleGetUserProfile()) return false;

    const mustShow = this.handleProduto(dados, hasContabil);

    if (!mustShow) return false;

    return mustShow;
  }

  async handleSalvarCampanha() {
    try {
      if (this.userNpsStore.params.nota == undefined) return;

      const params: IParamsSavePesquisaNps = {
        Nota: this.userNpsStore.params.nota,
        Comentario: this.userNpsStore.params.comentario,
        _id: this.userNpsStore.params.campanhaId || ""
      };

      const result = await this.pesquisaNpsGateway.handleSalvarNps(params);

      if (!result) {
        this.notification.showNotification(
          "Erro ao tentar salvar a resposta, tente novamente mais tarde."
        );
      }

      return result;
    } catch (_err) {
      this.notification.showNotification(
        "Erro ao tentar salvar a resposta, tente novamente mais tarde."
      );
    }
  }

  async handleSalvarContato(): Promise<boolean> {
    const { aceitaContato, telefone, isWhatsapp, campanhaId } =
      this.userNpsStore.params;

    try {
      if (aceitaContato) {
        const financasFeatureGateway = diContainer.get<IFinancasFeatureGateway>(
          DITypes.FINANCAS_FEATURES
        );
        const resposta = await financasFeatureGateway.atualizarTelefoneUsuario(
          telefone,
          isWhatsapp
        );

        if (!resposta?.Ok) {
          this.notification.showNotification(
            resposta?.Error ||
              "Não foi possível salvar seu telefone. Verifique o número e tente novamente."
          );

          return false;
        }

        const perfilAtual = this.userProfileMixin.handleGetUserProfile();
        if (perfilAtual) {
          this.storage.set(LocalStorageKeys.UserProfile, {
            ...perfilAtual,
            Telefone: telefone,
            isWhatsApp: isWhatsapp
          });
        }
      }

      const contatoSalvo = await this.pesquisaNpsGateway.handleSalvarContatoNps(
        {
          _id: campanhaId || "",
          AceitaContato: aceitaContato,
          Telefone: aceitaContato ? telefone : undefined,
          IsWhatsapp: aceitaContato ? isWhatsapp : undefined
        }
      );

      if (!contatoSalvo) {
        this.notification.showNotification(
          "Erro ao registrar sua resposta, tente novamente mais tarde."
        );

        return false;
      }

      return true;
    } catch (_err) {
      this.notification.showNotification(
        "Erro ao registrar sua resposta, tente novamente mais tarde."
      );

      return false;
    }
  }

  verifyCampanhaAtiva() {
    return this.campanhaAtiva;
  }

  obterCampanhaParams(): IParamsNps {
    return { ...this.userNpsStore.params };
  }
}
